import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { POSTransaction } from '../../types/pos';
import { formatAmount, formatNu } from '../../utils/formatAmount';
import { gstReceiptLineTag } from '../../utils/gst';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import { getReceiptPaymentLines } from './ReceiptPaymentMethods';
import { paymentMethodShortLabel } from './SplitPaymentEditor';
import './ReceiptPrint.css';

/** Embed the logo so the print iframe still has the image after the preview HTML is copied. */
function usePrintableLogo(src: string | null): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!src) {
      setDataUrl(null);
      return;
    }
    if (src.startsWith('data:')) {
      setDataUrl(src);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error('logo fetch failed');
        const blob = await res.blob();
        const reader = new FileReader();
        const url = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        if (!cancelled && url) setDataUrl(url);
      } catch {
        if (!cancelled) setDataUrl(src);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  return dataUrl;
}

type ReceiptPrintViewProps = {
  transaction: POSTransaction;
  storeName: string;
  className?: string;
  /** Company logo data URL. When omitted, the view loads the signed-in company's logo. */
  logoSrc?: string | null;
};

export const ReceiptPrintView: React.FC<ReceiptPrintViewProps> = ({
  transaction,
  storeName,
  className = '',
  logoSrc = null,
}) => {
  const items = transaction.items || [];
  const paymentLines = getReceiptPaymentLines(transaction);
  const itemsSubtotal = items.reduce(
    (sum, item) => sum + (Number(item.total_price) || 0),
    0,
  );
  const tax = Math.max(0, Number(transaction.tax_amount) || 0);
  const discount = Math.max(0, Number(transaction.discount_amount) || 0);
  const coupon = Math.max(0, Number(transaction.coupon_discount) || 0);
  const total = Number(transaction.total_amount) || 0;
  const saleDate = moment(transaction.sale_date).format('YYYY-MM-DD');
  const saleTime = transaction.sale_time || moment(transaction.created_at).format('hh:mm A');
  const hasCustomerGst = tax > 0.0005;
  const receiptGstRate =
    items.find((i) => i.gst_rate != null && Number(i.gst_rate) > 0)?.gst_rate ?? 5;
  const companyLogo = useCompanyLogo(null);
  const embeddedLogo = usePrintableLogo(logoSrc ? null : companyLogo);
  const shownLogo = logoSrc || embeddedLogo;

  return (
    <div className={`receipt-print-document ${className}`.trim()}>
      <header className="receipt-print-header">
        {shownLogo ? (
          <img className="receipt-print-logo" src={shownLogo} alt="Company logo" />
        ) : null}
        <div className="receipt-print-store">{storeName || 'Store'}</div>
        <div className="receipt-print-badge">
          {transaction.is_tax_invoice ? 'TAX INVOICE' : 'Sales Receipt'}
        </div>
      </header>

      <section className="receipt-print-meta">
        <div className="receipt-print-meta-row">
          <span>Receipt #</span>
          <strong>{transaction.transaction_number}</strong>
        </div>
        {transaction.tax_invoice_number ? (
          <div className="receipt-print-meta-row">
            <span>Tax invoice</span>
            <strong>{transaction.tax_invoice_number}</strong>
          </div>
        ) : null}
        {transaction.supplier_address ? (
          <div className="receipt-print-meta-row">
            <span>Address</span>
            <strong>{transaction.supplier_address}</strong>
          </div>
        ) : null}
        <div className="receipt-print-meta-row">
          <span>Date</span>
          <strong>{saleDate}</strong>
        </div>
        <div className="receipt-print-meta-row">
          <span>Time</span>
          <strong>{saleTime}</strong>
        </div>
        {transaction.serial ? (
          <div className="receipt-print-meta-row">
            <span>Serial</span>
            <strong>{transaction.serial}</strong>
          </div>
        ) : null}
        {transaction.cashier_name ? (
          <div className="receipt-print-meta-row">
            <span>Cashier</span>
            <strong>{transaction.cashier_name}</strong>
          </div>
        ) : null}
        {transaction.is_tax_invoice && (transaction.buyer_name || transaction.buyer_cid || transaction.buyer_tpn) ? (
          <>
            {transaction.buyer_name ? (
              <div className="receipt-print-meta-row">
                <span>Buyer</span>
                <strong>{transaction.buyer_name}</strong>
              </div>
            ) : null}
            {transaction.buyer_cid ? (
              <div className="receipt-print-meta-row">
                <span>CID</span>
                <strong>{transaction.buyer_cid}</strong>
              </div>
            ) : null}
            {transaction.buyer_tpn ? (
              <div className="receipt-print-meta-row">
                <span>Buyer TPN</span>
                <strong>{transaction.buyer_tpn}</strong>
              </div>
            ) : null}
            {transaction.buyer_address ? (
              <div className="receipt-print-meta-row">
                <span>Buyer address</span>
                <strong>{transaction.buyer_address}</strong>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      <table className="receipt-print-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item, idx) => {
              const unit = Number(item.sale_unit_price ?? item.unit_price) || 0;
              const lineTotal = Number(item.total_price) || 0;
              const code = item.barcode || item.product_code || '';
              const lineGst = Number(item.gst_amount) || 0;
              const showGstTag =
                transaction.is_tax_invoice &&
                (lineGst > 0.0005 ||
                  item.gst_category === 'exempt' ||
                  item.gst_category === 'zero_rated');
              return (
                <tr key={item.id ?? `${item.product_id}-${idx}`}>
                  <td>
                    <div className="receipt-print-item-name">{item.product_name}</div>
                    {code ? <div className="receipt-print-item-code">{code}</div> : null}
                    {showGstTag ? (
                      <div className="receipt-print-item-gst">
                        {gstReceiptLineTag(item.gst_category, item.gst_amount)}
                      </div>
                    ) : null}
                  </td>
                  <td className="receipt-print-qty">
                    {item.quantity} × {formatAmount(unit)}
                  </td>
                  <td className="receipt-print-amount">{formatNu(lineTotal)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={3} className="receipt-print-empty">
                No line items
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <section className="receipt-print-summary">
        <div className="receipt-print-summary-row">
          <span>Subtotal</span>
          <span>{formatNu(itemsSubtotal)}</span>
        </div>
        {discount > 0 ? (
          <div className="receipt-print-summary-row">
            <span>Discount</span>
            <span>-{formatNu(discount)}</span>
          </div>
        ) : null}
        {coupon > 0 ? (
          <div className="receipt-print-summary-row">
            <span>Coupon</span>
            <span>-{formatNu(coupon)}</span>
          </div>
        ) : null}
        {transaction.is_tax_invoice && hasCustomerGst ? (
          <>
            <div className="receipt-print-summary-row">
              <span>Taxable (ex-GST)</span>
              <span>{formatNu(Number(transaction.taxable_amount) || 0)}</span>
            </div>
            {Number(transaction.exempt_amount) > 0 ? (
              <div className="receipt-print-summary-row">
                <span>Exempt</span>
                <span>{formatNu(Number(transaction.exempt_amount) || 0)}</span>
              </div>
            ) : null}
            {Number(transaction.zero_rated_amount) > 0 ? (
              <div className="receipt-print-summary-row">
                <span>Zero-rated</span>
                <span>{formatNu(Number(transaction.zero_rated_amount) || 0)}</span>
              </div>
            ) : null}
            <div className="receipt-print-summary-row">
              <span>GST {receiptGstRate}% (included in prices above)</span>
              <span>{formatNu(tax)}</span>
            </div>
          </>
        ) : transaction.is_tax_invoice && Number(transaction.exempt_amount) > 0 ? (
          <div className="receipt-print-summary-row">
            <span>Exempt supplies</span>
            <span>{formatNu(Number(transaction.exempt_amount) || 0)}</span>
          </div>
        ) : !transaction.is_tax_invoice && tax > 0 ? (
          <div className="receipt-print-summary-row">
            <span>Tax</span>
            <span>{formatNu(tax)}</span>
          </div>
        ) : null}
        <div className="receipt-print-summary-row receipt-print-summary-total">
          <span>Total</span>
          <span>{formatNu(total)}</span>
        </div>
      </section>

      <section className="receipt-print-payments">
        <div className="receipt-print-payments-title">
          Payment{paymentLines.length > 1 ? 's' : ''}
        </div>
        {paymentLines.length > 0 ? (
          paymentLines.map((line, idx) => {
            const label = paymentMethodShortLabel(line.payment_method);
            const extras: string[] = [];
            if (line.reference_number) extras.push(`Jrn ${line.reference_number}`);
            if (line.payer_phone) extras.push(`Tel ${line.payer_phone}`);
            const ref = extras.length ? ` (${extras.join(', ')})` : '';
            const amount =
              paymentLines.length > 1 ? formatNu(line.amount) : formatNu(total);
            return (
              <div key={idx} className="receipt-print-payment-row">
                <span>
                  {label}
                  {ref}
                </span>
                <strong>{amount}</strong>
              </div>
            );
          })
        ) : (
          <div className="receipt-print-payment-row">
            <span>Cash</span>
            <strong>{formatNu(total)}</strong>
          </div>
        )}
      </section>

      <footer className="receipt-print-footer">
        {transaction.is_tax_invoice && hasCustomerGst ? (
          <div className="receipt-print-gst-notice">
            GST shown above is already included in the item prices. This shop
            remits collected GST to the Department of Revenue &amp; Customs.
          </div>
        ) : (
          'Thank you for your purchase!'
        )}
      </footer>
    </div>
  );
};
